import React from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { FileManagerItemList } from './FileManagerItem';
import { useLanguage } from '../../i18n';

const FileManagerList = ({
    folders,
    documents,
    selectedFolders,
    selectedDocuments,
    selectionMode,
    onSelect,
    onOpen,
    onContextMenu,
    sortBy,
    sortOrder,
    onSort,
    onDragStart,
    onDragEnd,
    onDrop,
    isDragging
}) => {
    const { t } = useLanguage();
    // 11px / 600 / 0.09em, meta-toned — the design's data-table header spec.
    // NOTE: a sorted header renders exactly ONE <svg> (the chevron) and an
    // unsorted one renders none; file-manager-prefs.spec.js counts them, so no
    // decorative icon may be added here.
    const HEAD_CELL = 'px-[22px] py-[14px] text-left text-[11px] font-semibold text-pai-meta uppercase tracking-[0.09em] whitespace-nowrap';

    const SortHeader = ({ field, label }) => (
        <th
            onClick={() => onSort(field)}
            className={`${HEAD_CELL} cursor-pointer select-none transition-colors hover:text-white`}
        >
            <div className="flex items-center gap-1">
                {label}
                {sortBy === field && (
                    // Clear Up/Down direction indicator for the active sort column
                    // (Date order ticket). data-direction makes the current order
                    // assertable and unambiguous (asc = ChevronUp, desc = ChevronDown).
                    <span data-testid={`fm-sort-indicator-${field}`} data-direction={sortOrder}>
                        {sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </span>
                )}
            </div>
        </th>
    );

    return (
        // The design gives the table an 820px floor and lets the pane scroll
        // horizontally rather than crushing the columns. The scroll is contained
        // by this wrapper, so it never widens the document (mobile #171).
        <div className="overflow-x-auto marquee-area rounded-card border border-pai bg-white/[0.025]">
            <table className="w-full min-w-[820px]">
                <thead>
                    <tr className="border-b border-pai-subtle">
                        <SortHeader field="name" label={t('commonName', 'Name')} />
                        <SortHeader field="size" label={t('commonSize', 'Size')} />
                        <th className={HEAD_CELL}>
                            {t('commonType', 'Type')}
                        </th>
                        {/* "Modified" → "Versions": shows v2+ only for PAI-edited docs
                            (Date&Version #81). Plus a brand-new "Date" = original
                            upload date. */}
                        <th className={HEAD_CELL}>
                            {t('fmVersionsColumn', 'Versions')}
                        </th>
                        <SortHeader field="date" label={t('commonDate', 'Date')} />
                    </tr>
                </thead>
                <tbody>
                    {/* Folders first */}
                    {folders.map((folder) => (
                        <FileManagerItemList
                            key={`folder-${folder.id}`}
                            item={folder}
                            type="folder"
                            isSelected={selectedFolders.has(folder.id)}
                            onSelect={onSelect}
                            onOpen={onOpen}
                            onContextMenu={onContextMenu}
                            onDragStart={onDragStart}
                            onDragEnd={onDragEnd}
                            onDrop={onDrop}
                            isDragging={isDragging}
                            selectedFolders={selectedFolders}
                            selectedDocuments={selectedDocuments}
                            selectionMode={selectionMode}
                        />
                    ))}

                    {/* Then documents */}
                    {documents.map((doc) => (
                        <FileManagerItemList
                            key={`doc-${doc.id}`}
                            item={doc}
                            type="document"
                            isSelected={selectedDocuments.has(doc.id)}
                            onSelect={onSelect}
                            onOpen={onOpen}
                            onContextMenu={onContextMenu}
                            onDragStart={onDragStart}
                            onDragEnd={onDragEnd}
                            onDrop={onDrop}
                            isDragging={isDragging}
                            selectedFolders={selectedFolders}
                            selectedDocuments={selectedDocuments}
                            selectionMode={selectionMode}
                        />
                    ))}

                    {/* Empty state */}
                    {folders.length === 0 && documents.length === 0 && (
                        <tr>
                            <td colSpan={5} className="px-[22px] py-12 text-center text-[15px] text-white/40">
                                {t('fmFolderEmpty', 'This folder is empty')}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default FileManagerList;
